import React, { useEffect, useRef, useState } from 'react';
import SupportChatWindow from './components/SupportChatWindow';
import { clientChangeRequests, clientProfile } from '../../mocks/client';
import { fetchMyRequests, submitRequest } from '../../lib/api/clientRequests';
import DataModeBadge from '../../components/feedback/DataModeBadge';

/**
 * Support page (full-live-cutover §2.2): the change-request form now WRITES —
 * submitRequest() persists a real client_requests row and routes it (dry-run)
 * to agent_0_orchestrator. The list is fetch-first: live rows on success,
 * the sample seed ONLY behind the labeled mode ('sample' badge) on failure —
 * never a silent mock (go-live-readiness §1 criterion 2).
 *
 * Submit runs optimistically (features/approvals/optimistic.js pattern):
 * the row is appended immediately and rolled back if the POST fails, with the
 * error surfaced in the form instead of swallowed.
 */
function ClientSupportPage() {
  // null = still fetching; [] = live empty; array = live rows (mode 'live');
  // fallback to the sample seed happens on fetch failure (mode 'sample').
  const [requests, setRequests] = useState(null);
  const [mode, setMode] = useState('live');
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  // Holds the optimistic row until the POST commits or rolls it back.
  const pendingRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchMyRequests()
      .then((rows) => {
        if (cancelled) return;
        setRequests(Array.isArray(rows) ? rows : []);
        setMode('live');
      })
      .catch(() => {
        // Sample seed ONLY behind the honest label — never a silent mock.
        if (cancelled) return;
        setRequests(clientChangeRequests);
        setMode('sample');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fileRequest = async (event) => {
    event.preventDefault();
    const trimmed = title.trim();
    if (!trimmed || busy) return;

    const optimistic = {
      id: `cr-local-${Date.now()}`,
      title: trimmed,
      status: 'under-review',
      filedOn: new Date().toISOString().slice(0, 10)
    };
    pendingRef.current = optimistic;
    setRequests((current) => [optimistic, ...(current || [])]);
    setTitle('');
    setDetail('');
    setBusy(true);
    setFormError('');

    try {
      const saved = await submitRequest({ subject: trimmed, body: detail.trim() || trimmed });
      pendingRef.current = null; // commit
      setRequests((current) => (current || []).map(
        (request) => (request.id === optimistic.id
          ? {
            id: saved.id,
            title: saved.subject,
            status: saved.status === 'routed' ? 'under-review' : 'submitted',
            filedOn: (saved.created || new Date().toISOString()).slice(0, 10)
          }
          : request)
      ));
    } catch (submitError) {
      pendingRef.current = null; // rollback
      setRequests((current) => (current || []).filter((request) => request.id !== optimistic.id));
      setTitle(trimmed); // restore the draft — the user's text is not lost
      setFormError(submitError.message || 'Could not file the request — try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="portal-page">
      <header className="portal-page-head">
        <div>
          <p className="eyebrow">SUPPORT</p>
          <h1>
            We&apos;re on it <DataModeBadge mode={mode} label="Sample requests — backend unreachable" />
          </h1>
          <span>
            Your account manager is {clientProfile.accountManager.name}
            ({clientProfile.accountManager.role}) — Agent 9 handles the rest.
          </span>
        </div>
      </header>

      <div className="portal-columns">
        <div className="portal-col-main">
          <SupportChatWindow />
        </div>
        <aside className="portal-col-side">
          <section className="portal-card">
            <div className="portal-card-head">
              <div>
                <h2>Request a change</h2>
                <span>Agents, alerts, limits — we configure, you approve</span>
              </div>
            </div>
            <form className="portal-request-form" onSubmit={fileRequest}>
              <label htmlFor="request-title">What should change?</label>
              <input
                id="request-title"
                onChange={(event) => setTitle(event.target.value)}
                placeholder="e.g. Add SMS alerts for late shipments"
                value={title}
              />
              <label htmlFor="request-detail">Any detail worth adding?</label>
              <textarea
                id="request-detail"
                onChange={(event) => setDetail(event.target.value)}
                placeholder="Context, affected agents, deadlines…"
                rows={3}
                value={detail}
              />
              <button disabled={busy} type="submit">{busy ? 'Filing…' : 'File request'}</button>
              {formError && <span className="portal-error" role="alert">{formError}</span>}
            </form>
            <ul className="portal-request-list">
              {(requests || []).map((request) => (
                <li key={request.id}>
                  <p>{request.title}</p>
                  <span>Filed {request.filedOn} · {String(request.status).replace('-', ' ')}</span>
                </li>
              ))}
              {requests !== null && !requests.length && (
                <li className="portal-empty">No open requests.</li>
              )}
              {requests === null && (
                <li className="portal-empty">Loading requests…</li>
              )}
            </ul>
          </section>
        </aside>
      </div>
    </main>
  );
}

export default ClientSupportPage;
